import { useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, ZAxis, PieChart, Pie, CartesianGrid,
} from "recharts";
import "./HBMConsult.css";

export const asArr = (v) => (Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : []);
export const finite = (v) => Number.isFinite(Number(v));
export const fmt = (v) => {
  const n = Number(v);
  if (!finite(n)) return String(v ?? "—");
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (abs >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (abs >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
export const ACCENT = "#0000ff";

function MetricsStrip({ metrics }) {
  if (!metrics?.length) return null;
  return (
    <div className="hbmc-metrics" data-testid="consult-metrics">
      {metrics.map((m, i) => (<div key={i}><span>{m.label}</span><b>{m.value}</b><em>{m.delta}</em></div>))}
    </div>
  );
}

function Section({ kicker, title, desc, badge, children, id }) {
  return (
    <section className="hbmc-sec" id={id} data-testid={id || undefined}>
      <header>
        <div>
          <div className="kicker">{kicker}</div>
          <h2 className="sec">{title}</h2>
          {desc && <p className="muted">{desc}</p>}
        </div>
        {badge && <div className="hbmc-badge">{badge}</div>}
      </header>
      {children}
    </section>
  );
}

function ConfidenceHeader({ c }) {
  if (!c || !finite(c.overall)) return null;
  const pct = Math.max(0, Math.min(100, Number(c.overall)));
  const R = 34, C = 2 * Math.PI * R, off = C - (pct / 100) * C;
  const band = c.band || (pct >= 75 ? "High" : pct >= 50 ? "Moderate" : "Developing");
  const col = pct >= 75 ? "var(--success)" : pct >= 50 ? ACCENT : "var(--danger)";
  return (
    <div className="hbmc-card confHeader" data-testid="confidence-header" style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ position: "relative", width: 92, height: 92, flexShrink: 0 }}>
        <svg width="92" height="92" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={R} fill="none" stroke="var(--bg-pill)" strokeWidth="8" />
          <circle cx="40" cy="40" r={R} fill="none" stroke={col} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={off} transform="rotate(-90 40 40)" style={{ transition: "stroke-dashoffset .8s ease" }} />
          <text x="40" y="38" textAnchor="middle" fontFamily="Instrument Serif,serif" fontSize="20" fill="var(--text)">{pct}%</text>
          <text x="40" y="52" textAnchor="middle" fontFamily="JetBrains Mono,monospace" fontSize="6" letterSpacing="1.5" fill="var(--text-dim)">CONFIDENCE</text>
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div className="kicker">Overall decision confidence · {band}</div>
        <div className="signalCols" style={{ gridTemplateColumns: "repeat(4,1fr)", marginTop: 10, gap: 14 }}>
          {[["Evidence coverage", c.evidenceCoverage], ["Calculation coverage", c.calculationCoverage], ["Research quality", c.researchQuality], ["Reconciled facts", c.reconciledFacts]].map(([k, v]) => (
            <div key={k}><span style={{ marginBottom: 4 }}>{k}</span><b style={{ fontFamily: "Instrument Serif,serif", fontSize: 22 }}>{finite(v) ? (String(k).includes("facts") ? v : `${Math.round(v)}%`) : "—"}</b></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DecisionDashboard({ a }) {
  const ai = a.deepAnalysis || {};
  const fm = a.financialModel || {};
  const mets = asArr(fm.metrics).filter((m) => finite(m.actual));
  const max = Math.max(...mets.map((m) => Math.abs(Number(m.actual))), 1);
  return (
    <Section kicker="HBM Decision Dashboard" title={ai.executiveJudgement ? "Executive judgement" : "Evidence analysis and decision signals"}
      desc={ai.executiveJudgement} badge={a.engineStatus?.connected ? "ANALYSIS ACTIVE" : "EVIDENCE FALLBACK"} id="dashboard">
      <div className="hbmc-grid g2">
        <div className="hbmc-card">
          <div className="lbl"><span>Reconciled financial actuals</span><b>{a.sector || "Business"}</b></div>
          {mets.length ? mets.slice(0, 8).map((m) => {
            const v = Number(m.actual);
            return (
              <div className="metricBar" key={m.name}>
                <div><b>{m.name}</b><strong>{fmt(v)}</strong></div>
                <div className="barTrack"><i style={{ width: `${Math.max(6, Math.abs(v) / max * 100)}%` }} /></div>
                <small>{m.period ? `${m.period} · ` : ""}{m.confidence ?? 0}% confidence</small>
              </div>
            );
          }) : <p className="muted">No reliably labelled financial values were found.</p>}
        </div>
        <div className="hbmc-card">
          <div className="lbl"><span>Model judgement</span><b>{a.reasoningMode || ""}</b></div>
          <h3 className="sub">{ai.triangulationSummary?.robustConclusion || ai.executiveJudgement || "Deep reasoning was not returned."}</h3>
          <div className="signalCols" style={{ marginTop: 16 }}>
            <div><span>Catalysts</span>{asArr(ai.catalysts).slice(0, 4).map((x, i) => <p key={i}>+ {String(x)}</p>)}</div>
            <div><span>Risks</span>{asArr(ai.risks).slice(0, 4).map((x, i) => <p key={i}>− {String(x)}</p>)}</div>
          </div>
        </div>
      </div>
      <div className="hbmc-card" style={{ marginTop: 14 }}>
        <div className="lbl"><span>Management actions</span><b>Prioritised</b></div>
        <ol className="actionList">
          {(asArr(ai.managementActions).length ? asArr(ai.managementActions) : ["Validate source figures and reporting periods.", "Define measurable decision gates."])
            .slice(0, 6).map((x, i) => <li key={i}><i>{String(i + 1).padStart(2, "0")}</i><span>{String(x)}</span></li>)}
        </ol>
      </div>
    </Section>
  );
}

function DecisionIntelligence({ a }) {
  const ai = a.deepAnalysis || {};
  const scores = asArr(ai.decisionScorecard).filter((s) => finite(s.score) && Number(s.weight) > 0);
  const totalW = scores.reduce((n, s) => n + (Number(s.weight) || 0), 0);
  const weighted = scores.length && totalW ? Math.round(scores.reduce((n, s) => n + Number(s.score) * Number(s.weight), 0) / totalW) : 0;
  const outlook = ai.probabilisticOutlook || {};
  const cases = [["Downside", outlook.downside], ["Base", outlook.base], ["Upside", outlook.upside]];
  const cov = ai.evidenceCoverage || {};
  return (
    <Section kicker="Evidence-admissible decision engine"
      title={scores.length ? `${scores.length} supported indicators · weighted score ${weighted}/100` : "Composite score withheld"}
      badge={outlook.forecastHorizon || "Decision horizon"} id="decision-intel">
      {scores.length ? (
        <div className="hbmc-grid g2">
          {scores.map((s, i) => (
            <div className={`hbmc-card sig-${(s.signal || "neutral").toLowerCase()}`} key={i}>
              <div className="scoreRow">
                <div className="top"><b>{s.parameter}</b><em>{s.weight}% weight · {s.confidence ?? "—"}% conf</em></div>
                <div className="scoreTrack"><i style={{ width: `${Math.max(0, Math.min(100, s.score || 0))}%` }} /></div>
                <p>{s.evidence}</p>
                <small>{s.source || "Uploaded evidence"}{s.location ? ` · ${s.location}` : ""}</small>
                {s.calculation && <small>Calculation: {s.calculation}</small>}
              </div>
            </div>
          ))}
        </div>
      ) : <div className="hbmc-card"><p className="muted">No parameter carried sufficient document-linked evidence and disclosed calculation to justify a score.</p></div>}

      {cases.some(([, c]) => finite(c?.probability)) && (
        <>
          <div className="probGrid" style={{ marginTop: 16 }}>
            {cases.map(([name, c]) => (
              <div className={`probCard ${name.toLowerCase()}`} key={name}>
                <span>{name.toUpperCase()} CASE</span>
                <b>{finite(c?.probability) ? `${c.probability}%` : "—"}</b>
                <h4>{c?.outcome || "Not quantified"}</h4>
                <p><strong>Trigger:</strong> {c?.trigger || "Additional evidence required"}</p>
              </div>
            ))}
          </div>
          <p className="dim" style={{ fontSize: 12, marginTop: 8 }}>METHOD · {outlook.method || "Scenario method not stated."}</p>
        </>
      )}

      <div className="hbmc-grid g3" style={{ marginTop: 16 }}>
        <div className="hbmc-card">
          <div className="lbl"><span>Sensitivity & elasticity</span></div>
          {asArr(ai.sensitivityAnalysis).slice(0, 8).map((s, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <b style={{ fontSize: 13 }}>{s.driver}</b>
              <p className="dim" style={{ fontSize: 12, margin: "2px 0" }}>{s.downsideChange || "—"} / {s.baseChange || "—"} / {s.upsideChange || "—"}</p>
              <small className="dim">{s.elasticity || s.impact || ""}</small>
            </div>
          ))}
          {!asArr(ai.sensitivityAnalysis).length && <p className="dim" style={{ fontSize: 12 }}>Not established.</p>}
        </div>
        <div className="hbmc-card">
          <div className="lbl"><span>Leading indicators & gates</span></div>
          {asArr(ai.leadingIndicators).slice(0, 8).map((s, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <b style={{ fontSize: 13 }}>{s.indicator}</b>
              <p style={{ fontSize: 12, margin: "2px 0" }}>{s.current || "n/a"} → <strong>{s.target || "target"}</strong></p>
              <small className="dim">Warn {s.warningThreshold || "n/a"} · {s.cadence || "cadence"} · {s.owner || "owner"}</small>
            </div>
          ))}
          {!asArr(ai.leadingIndicators).length && <p className="dim" style={{ fontSize: 12 }}>Not established.</p>}
        </div>
        <div className="hbmc-card">
          <div className="lbl"><span>Red-team challenges</span></div>
          {asArr(ai.redTeamChallenges).slice(0, 6).map((s, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <b style={{ fontSize: 13 }}>{s.challenge}</b>
              <p style={{ fontSize: 12, margin: "2px 0" }}>Disconfirm with: {s.disconfirmingEvidence || "independent evidence"}</p>
              <small className="dim">{s.decisionImpact}</small>
            </div>
          ))}
          {!asArr(ai.redTeamChallenges).length && <p className="dim" style={{ fontSize: 12 }}>None raised.</p>}
        </div>
      </div>

      <div className="hbmc-metrics" style={{ marginTop: 16 }}>
        {[["Document facts", cov.documentFacts], ["External facts", cov.externalFacts], ["Calculations", cov.calculations], ["Coverage", `${cov.coverageScore ?? ai.confidenceScore ?? 0}%`]]
          .map(([k, v]) => <div key={k}><span>{k}</span><b>{v ?? 0}</b></div>)}
      </div>
    </Section>
  );
}

function PeerBenchmark({ pb }) {
  if (!pb) return null;
  if (pb.mode === "qualitative" || (!asArr(pb.rows).length && asArr(pb.peers).length)) {
    const peers = asArr(pb.peers);
    return (
      <Section kicker="Peer benchmark" title="Named-competitor positioning" desc={pb.basis} badge={`${peers.length} peers`} id="peers">
        {(pb.subjectEdge || pb.subjectRisk) && (
          <div className="hbmc-grid g2" style={{ marginBottom: 14 }}>
            {pb.subjectEdge && <div className="hbmc-card"><span className="kicker">Subject edge</span><p style={{ fontSize: 13, margin: "6px 0 0" }}>{pb.subjectEdge}</p></div>}
            {pb.subjectRisk && <div className="hbmc-card"><span className="kicker">Subject risk</span><p style={{ fontSize: 13, margin: "6px 0 0" }}>{pb.subjectRisk}</p></div>}
          </div>
        )}
        <div className="hbmc-grid g2" data-testid="peer-qualitative">
          {peers.map((p, i) => (
            <div className="hbmc-card" key={i}>
              <div className="lbl"><span>{p.relativeScale || "peer"}</span><b>{p.sharedMarket || ""}</b></div>
              <h3 className="sub" style={{ fontSize: 16 }}>{p.name}</h3>
              <p style={{ fontSize: 13, margin: "4px 0" }}>{p.positioning}</p>
              <p style={{ fontSize: 12, margin: "4px 0" }}><strong>Edge:</strong> {p.strength || "—"}</p>
              <p style={{ fontSize: 12, margin: 0 }}><strong>Gap:</strong> {p.weakness || "—"}</p>
            </div>
          ))}
        </div>
        {pb.benchmarkNote && <p className="dim" style={{ fontSize: 12, marginTop: 10 }}>{pb.benchmarkNote}</p>}
      </Section>
    );
  }
  const rows = asArr(pb.rows);
  if (!rows.length) return null;
  const metrics = asArr(pb.metrics);
  const ranking = pb.ranking || {};
  const subject = rows.find((r) => r.isSubject);
  const chartMetric = "Net margin %";
  const chartData = rows.filter((r) => finite(r.values?.[chartMetric])).map((r) => ({ name: (r.ticker || r.company).slice(0, 8), value: Number(r.values[chartMetric]), subj: r.isSubject }));
  return (
    <Section kicker="Peer benchmark" title="How the subject ranks against listed peers" desc={pb.basis} badge={`${rows.length} companies`} id="peers">
      <div className="hbmc-card" style={{ overflowX: "auto" }}>
        <table className="tableSmall" data-testid="peer-table">
          <thead><tr><th>Company</th>{metrics.map((m) => <th key={m} style={{ textAlign: "right" }}>{m}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={r.isSubject ? { background: "var(--bg-glow)", fontWeight: 600 } : {}}>
                <td>{r.isSubject ? "★ " : ""}{r.company} <span className="dim" style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 10 }}>{r.ticker}</span></td>
                {metrics.map((m) => {
                  const v = r.values?.[m];
                  return <td key={m} style={{ textAlign: "right", fontFamily: "JetBrains Mono,monospace" }}>{finite(v) ? (m === "Revenue" ? fmt(v) : `${Number(v).toFixed(1)}%`) : "—"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="hbmc-grid g2" style={{ marginTop: 14 }}>
        {subject && (
          <div className="hbmc-card">
            <div className="lbl"><span>Subject rank per metric</span><b>{subject.company}</b></div>
            {metrics.map((m) => {
              const rk = ranking[m];
              if (!rk) return null;
              return (
                <div className="scoreRow" key={m}>
                  <div className="top"><b style={{ fontSize: 13 }}>{m}</b><em>#{rk.rank} of {rk.of}</em></div>
                  <div className="scoreTrack"><i style={{ width: `${(1 - (rk.rank - 1) / Math.max(1, rk.of - 1)) * 100}%`, background: rk.rank === 1 ? "var(--success)" : ACCENT }} /></div>
                </div>
              );
            })}
          </div>
        )}
        {chartData.length > 0 && (
          <div className="hbmc-card">
            <div className="lbl"><span>{chartMetric} · peer comparison</span></div>
            <div className="chartWrap"><ResponsiveContainer>
              <BarChart data={chartData}><CartesianGrid strokeDasharray="2 2" stroke="rgba(27,31,39,.08)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="value">{chartData.map((e, i) => <Cell key={i} fill={e.subj ? ACCENT : "#9ca3af"} />)}</Bar>
              </BarChart></ResponsiveContainer></div>
          </div>
        )}
      </div>
    </Section>
  );
}

function AdvancedCharts({ a }) {
  const fm = a.financialModel || {};
  const ai = a.deepAnalysis || {};
  const calc = asArr(a.quantitativeAnalysis?.calculations);
  const margins = calc.filter((c) => c.unit === "%").map((c) => ({ name: c.name, value: Number(c.value) }));
  const get = (n) => { const x = asArr(fm.metrics).find((m) => String(m.name).toLowerCase() === n.toLowerCase()); return x && finite(x.actual) ? Number(x.actual) : null; };
  const ocf = get("Operating cash flow"), capex = get("Capital expenditure");
  const cashBridge = [ocf != null && { name: "Operating cash", value: ocf }, capex != null && { name: "Capex", value: -Math.abs(capex) }, ocf != null && capex != null && { name: "Free cash flow", value: ocf - capex }].filter(Boolean);
  const scores = asArr(ai.decisionScorecard).filter((s) => finite(s.score)).map((s) => ({ name: s.parameter, Score: Number(s.score), Weight: Number(s.weight), z: Number(s.weight) }));
  const sq = ai.sourceQualitySummary || {};
  const srcMix = [["Primary", sq.primarySources], ["Independent", sq.independentSources], ["Company", sq.companySources], ["News", sq.newsSources], ["Academic", sq.academicSources]]
    .filter(([, v]) => Number(v) > 0).map(([name, value]) => ({ name, value: Number(value) }));
  const PALETTE = ["#0000ff", "#1B1F27", "#6b7280", "#16a34a", "#b91c1c"];
  const has = margins.length || cashBridge.length || scores.length || srcMix.length;
  if (!has) return null;
  return (
    <Section kicker="Advanced analytical views" title="Calculated insights from reconciled evidence"
      desc="Every view is a calculation on admitted facts — not an illustration." id="charts">
      <div className="hbmc-grid g2">
        {margins.length > 0 && (
          <div className="hbmc-card">
            <div className="lbl"><span>Profitability & reinvestment (%)</span></div>
            <div className="chartWrap"><ResponsiveContainer>
              <BarChart data={margins} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="rgba(27,31,39,.08)" />
                <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="value" fill={ACCENT} />
              </BarChart></ResponsiveContainer></div>
          </div>
        )}
        {cashBridge.length > 0 && (
          <div className="hbmc-card">
            <div className="lbl"><span>Cash conversion bridge</span></div>
            <div className="chartWrap"><ResponsiveContainer>
              <BarChart data={cashBridge}><CartesianGrid strokeDasharray="2 2" stroke="rgba(27,31,39,.08)" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="value">{cashBridge.map((e, i) => <Cell key={i} fill={e.value < 0 ? "#b91c1c" : ACCENT} />)}</Bar>
              </BarChart></ResponsiveContainer></div>
          </div>
        )}
        {scores.length > 0 && (
          <div className="hbmc-card">
            <div className="lbl"><span>Decision drivers · score × weight</span></div>
            <div className="chartWrap"><ResponsiveContainer>
              <ScatterChart margin={{ left: 6, bottom: 6 }}><CartesianGrid stroke="rgba(27,31,39,.08)" />
                <XAxis type="number" dataKey="Score" name="Score" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis type="number" dataKey="Weight" name="Weight" tick={{ fontSize: 11 }} />
                <ZAxis type="number" dataKey="z" range={[60, 400]} />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [v, n]} labelFormatter={() => ""} />
                <Scatter data={scores} fill={ACCENT} />
              </ScatterChart></ResponsiveContainer></div>
          </div>
        )}
        {srcMix.length > 0 && (
          <div className="hbmc-card">
            <div className="lbl"><span>Research evidence composition</span></div>
            <div className="chartWrap"><ResponsiveContainer>
              <PieChart><Pie data={srcMix} dataKey="value" nameKey="name" outerRadius={100} label={(e) => `${e.name} (${e.value})`}>
                {srcMix.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie><Tooltip />
              </PieChart></ResponsiveContainer></div>
          </div>
        )}
      </div>
    </Section>
  );
}

function CrunchingLab({ a }) {
  const qa = a.quantitativeAnalysis || {};
  const ra = a.researchAnalytics || {};
  const calc = asArr(qa.calculations), clusters = asArr(ra.clusters);
  if (!calc.length && !clusters.length) return null;
  return (
    <Section kicker="HBM analytical workbench" title="Numbers, news and evidence converted into decision signals"
      badge={`${calc.length} calculations · ${ra.qualityScore || 0}% research quality`} id="crunch">
      <div className="hbmc-grid g3">
        <div className="hbmc-card"><span className="kicker">Calculation coverage</span><b style={{ display: "block", fontFamily: "Instrument Serif,serif", fontSize: 30 }}>{qa.coverage?.score || 0}%</b><p className="dim" style={{ fontSize: 12 }}>{qa.coverage?.available || 0} of {qa.coverage?.possible || 0} core calculations for {qa.period || "the period"}.</p></div>
        <div className="hbmc-card"><span className="kicker">Research inspection</span><b style={{ display: "block", fontFamily: "Instrument Serif,serif", fontSize: 30 }}>{ra.inspectionRate || 0}%</b><p className="dim" style={{ fontSize: 12 }}>{ra.inspected || 0} of {ra.total || 0} sources inspected across {ra.domains || 0} domains.</p></div>
        <div className="hbmc-card"><span className="kicker">Freshness</span><b style={{ display: "block", fontFamily: "Instrument Serif,serif", fontSize: 30 }}>{ra.freshnessRate || 0}%</b><p className="dim" style={{ fontSize: 12 }}>{ra.fresh || 0} sources within the latest 180-day window.</p></div>
      </div>
      <div className="hbmc-grid g2" style={{ marginTop: 14 }}>
        <div>
          <h3 className="sub" style={{ marginBottom: 10 }}>Financial calculation ledger</h3>
          {calc.map((c, i) => (
            <div className="hbmc-card" key={i} style={{ marginBottom: 8 }}>
              <div className="lbl"><span>{c.name}</span><b>{fmt(c.value)} {c.unit}</b></div>
              <p style={{ fontSize: 13, margin: 0 }}>{c.interpretation}</p>
              <small className="dim">{c.formula} · {c.confidence}% confidence</small>
            </div>
          ))}
        </div>
        <div>
          <h3 className="sub" style={{ marginBottom: 10 }}>News & research signal clusters</h3>
          {clusters.map((c, i) => (
            <div className="hbmc-card" key={i} style={{ marginBottom: 8 }}>
              <div className="lbl"><span>{c.theme}</span><b>{c.count} sources</b></div>
              <p className="dim" style={{ fontSize: 12, margin: "0 0 6px" }}>{c.supportive} supportive · {c.adverse} adverse · {c.mixed} mixed</p>
              <div style={{ height: 6, background: "var(--bg-pill)", display: "flex" }}>
                <i style={{ width: `${c.count ? c.supportive / c.count * 100 : 0}%`, background: "var(--success)" }} />
                <i style={{ width: `${c.count ? c.adverse / c.count * 100 : 0}%`, background: "var(--danger)" }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {asArr(ra.contradictoryThemes).length > 0 && (
        <div className="hbmc-card" style={{ marginTop: 14, borderColor: "var(--danger)" }}>
          <span className="kicker" style={{ color: "var(--danger)" }}>Contradictory evidence detected</span>
          <p style={{ margin: "6px 0 0", fontSize: 13 }}>{asArr(ra.contradictoryThemes).join(" · ")}</p>
        </div>
      )}
    </Section>
  );
}

function ResearchConsole({ a }) {
  const ai = a.deepAnalysis || {};
  const market = ai.marketSizing || {};
  const segments = asArr(ai.customerSegments).slice(0, 4);
  const competitors = asArr(ai.competitiveLandscape).slice(0, 5);
  const em = ai.economicModel || {};
  const hasMarket = market.tam && String(market.tam).toLowerCase() !== "unknown";
  return (
    <Section kicker="Research mandate" title={`${a.entity?.company || a.company} · ${a.entity?.exchangeSymbol || a.sector}`}
      desc={`${a.contentProfile?.documentType || "evidence document"} · identity confidence ${a.entity?.confidence ?? 0}%`}
      badge={`${a.researchTrace?.length || 0} searches · ${a.research?.length || 0} sources`} id="research">
      <div className="hbmc-grid g3">
        <div className="hbmc-card">
          <div className="lbl"><span>Market sizing</span></div>
          <div className="hbmc-grid g3" style={{ gap: 8 }}>
            {[["TAM", market.tam], ["SAM", market.sam], ["SOM", market.som]].map(([k, v]) => (
              <div key={k}><small className="dim">{k}</small><b style={{ display: "block", fontSize: 14 }}>{hasMarket ? (v || "—") : "Not evidenced"}</b></div>
            ))}
          </div>
          <p className="dim" style={{ fontSize: 12, marginTop: 8 }}>{market.method && String(market.method).toLowerCase() !== "unknown" ? market.method : "An auditable market-sizing method was not present in the evidence."}</p>
        </div>
        <div className="hbmc-card">
          <div className="lbl"><span>Revenue drivers</span></div>
          {asArr(em.revenueDrivers).slice(0, 6).map((x, i) => <p key={i} style={{ fontSize: 13, margin: "0 0 4px" }}><i style={{ color: "var(--success)" }}>+</i> {String(x)}</p>)}
          {!asArr(em.revenueDrivers).length && <p className="dim" style={{ fontSize: 12 }}>Not established.</p>}
        </div>
        <div className="hbmc-card">
          <div className="lbl"><span>Cost drivers</span></div>
          {asArr(em.costDrivers).slice(0, 6).map((x, i) => <p key={i} style={{ fontSize: 13, margin: "0 0 4px" }}><i style={{ color: "var(--danger)" }}>−</i> {String(x)}</p>)}
          {!asArr(em.costDrivers).length && <p className="dim" style={{ fontSize: 12 }}>Not established.</p>}
        </div>
      </div>

      {(segments.length > 0 || competitors.length > 0) && (
        <div className="hbmc-grid g2" style={{ marginTop: 14 }}>
          {segments.length > 0 && (
            <div className="hbmc-card"><div className="lbl"><span>Buyer segments & budgets</span></div>
              {segments.map((s, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <b style={{ fontSize: 13 }}>{s.name}</b> <span className="dim" style={{ fontSize: 12 }}>{s.budget || "budget not evidenced"}</span>
                  <p style={{ fontSize: 12, margin: "2px 0" }}>{s.need}</p>
                  <small className="dim">Adoption barrier: {s.adoptionBarrier || "not established"}</small>
                </div>
              ))}
            </div>
          )}
          {competitors.length > 0 && (
            <div className="hbmc-card"><div className="lbl"><span>Competitive landscape</span></div>
              {competitors.map((c, i) => (
                <div key={i} style={{ marginBottom: 10 }}>
                  <b style={{ fontSize: 13 }}>{c.name}</b> <span className="dim" style={{ fontSize: 12 }}>{c.type}</span>
                  <p style={{ fontSize: 12, margin: "2px 0" }}><strong>Edge:</strong> {c.strength || "—"} · <strong>Gap:</strong> {c.weakness || "—"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <h3 className="sub" style={{ margin: "18px 0 10px" }}>Agent research operations</h3>
      {asArr(a.researchTrace).map((step) => {
        const sources = asArr(a.research).filter((r) => r.label === step.label);
        return (
          <details className="researchOp" key={step.step}>
            <summary>
              <i>{String(step.step).padStart(2, "0")}</i>
              <div><b>{step.label}</b><span>{step.query}</span></div>
              <em>{sources.length || step.results} results ＋</em>
            </summary>
            <div className="researchOpBody">
              {sources.length ? sources.slice(0, 8).map((r, i) => (
                <a className="srcRow" href={r.url} target="_blank" rel="noreferrer" key={i}>
                  <i>{String(i + 1).padStart(2, "0")}</i>
                  <span><b>{r.title}</b><small>{r.domain}{r.published ? ` · ${r.published}` : ""}</small></span>
                  <em>OPEN <ExternalLink size={11} style={{ display: "inline" }} /></em>
                </a>
              )) : <p className="dim" style={{ fontSize: 12, padding: "8px 0" }}>No source retained from this stream after quality screening.</p>}
            </div>
          </details>
        );
      })}
    </Section>
  );
}

function MethodologyLab({ a }) {
  const ai = a.deepAnalysis || {};
  const clues = asArr(ai.clueLedger), methods = asArr(ai.methodologyMatrix), tri = ai.triangulationSummary || {};
  if (!clues.length && !methods.length) return null;
  return (
    <Section kicker="Multi-method analysis lab" title={`${clues.length} clues → ${methods.length} independent methods`}
      desc="Methods are selected from the document evidence, not applied as a generic checklist." id="methodology">
      <div className="hbmc-grid g3">
        <div className="hbmc-card"><span className="kicker">Robust conclusion</span><p style={{ fontSize: 13 }}>{tri.robustConclusion || "Awaiting agreement across methods."}</p><small className="dim">{tri.agreement}</small></div>
        <div className="hbmc-card"><span className="kicker">Conflicts</span><p style={{ fontSize: 13 }}>{tri.conflicts || "No material conflict reported."}</p><small className="dim">{tri.whatWouldChangeTheConclusion}</small></div>
        <div className="hbmc-card"><span className="kicker">Fragile conclusion</span><p style={{ fontSize: 13 }}>{tri.fragileConclusion || "None identified."}</p></div>
      </div>
      <div className="hbmc-grid g2" style={{ marginTop: 14 }}>
        {methods.slice(0, 12).map((m, i) => (
          <div className="hbmc-card" key={i}>
            <div className="lbl"><span>{m.category || "analytical method"}</span><b>{m.confidence ?? 0}%</b></div>
            <h3 className="sub" style={{ fontSize: 16 }}>{m.method}</h3>
            <p style={{ fontSize: 13 }}>{m.whySelected}</p>
            <small className="dim">Test: {m.calculationOrTest || "—"}</small><br />
            <small className="dim">Output: {typeof m.output === "object" ? JSON.stringify(m.output) : String(m.output || "pending")}</small>
          </div>
        ))}
      </div>
      {clues.length > 0 && (
        <div className="hbmc-card" style={{ marginTop: 14 }}>
          <div className="lbl"><span>Document clue ledger</span><b>{clues.length} traceable signals</b></div>
          {clues.slice(0, 20).map((c, i) => (
            <div className="srcRow" key={i} style={{ cursor: "default" }}>
              <i>{String(i + 1).padStart(2, "0")}</i>
              <span><b>{c.clue}</b><small>{[c.domain, c.source, c.location, `${c.confidence ?? 0}%`].filter(Boolean).join(" · ")}</small></span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function ResearchAudit({ a }) {
  const ai = a.deepAnalysis || {};
  const claims = asArr(ai.claimAudit), q = ai.sourceQualitySummary || {};
  if (!claims.length && !Object.keys(q).length) return null;
  return (
    <Section kicker="Deep research audit" title={`${claims.length} claims challenged`}
      desc="Every material claim is tested for both supporting and opposing evidence." badge={`${q.qualityScore ?? 0}% source quality`} id="audit">
      <div className="hbmc-metrics">
        {[["Primary", q.primarySources], ["Independent", q.independentSources], ["Academic", q.academicSources], ["News", q.newsSources]]
          .map(([k, v]) => <div key={k}><span>{k}</span><b>{v ?? 0}</b></div>)}
      </div>
      <div className="hbmc-grid g2" style={{ marginTop: 14 }}>
        {claims.slice(0, 16).map((c, i) => (
          <div className="hbmc-card" key={i}>
            <div className="lbl"><span>Claim audit</span><b>{c.confidence ?? 0}%</b></div>
            <h3 className="sub" style={{ fontSize: 16 }}>{c.claim}</h3>
            <p style={{ fontSize: 12, margin: "4px 0" }}><b>For:</b> {asArr(c.evidenceFor).map(String).join(" · ") || "none found"}</p>
            <p style={{ fontSize: 12, margin: "4px 0" }}><b>Against:</b> {asArr(c.evidenceAgainst).map(String).join(" · ") || "none found"}</p>
            <small className="dim">Verdict: {c.verdict || "unresolved"}</small>
          </div>
        ))}
      </div>
    </Section>
  );
}

function EvidenceLedger({ a }) {
  const facts = asArr(a.factLedger), signals = asArr(a.transcriptSignals);
  if (!facts.length && !signals.length) return null;
  return (
    <Section kicker="Reconciled fact & transcript ledger" title={`${facts.length} facts · ${signals.length} management signals`}
      desc="Facts carry period, unit and provenance checks before admission." id="ledger">
      <div className="hbmc-grid g3">
        {facts.slice(0, 12).map((f, i) => (
          <div className="hbmc-card" key={i}>
            <div className="lbl"><span>{f.kind || "fact"}</span><b>{f.confidence ?? 0}%</b></div>
            <h3 className="sub" style={{ fontSize: 15 }}>{f.metric}</h3>
            <p style={{ fontSize: 14, margin: "4px 0" }}><b>{finite(f.value) ? fmt(f.value) : String(f.value)} {f.unit || ""}</b></p>
            <small className="dim">{f.period || "period not stated"} · {f.source || "uploaded evidence"}</small>
          </div>
        ))}
      </div>
      {signals.length > 0 && (
        <div className="hbmc-grid g2" style={{ marginTop: 14 }}>
          {signals.slice(0, 6).map((s, i) => (
            <div className="hbmc-card" key={i}>
              <div className="lbl"><span>transcript</span><b>{s.confidence ?? 0}%</b></div>
              <h3 className="sub" style={{ fontSize: 15 }}>{s.topic}</h3>
              <p style={{ fontSize: 13 }}>{s.statement}</p>
              <small className="dim">{s.speaker || "Management"} · {s.impact || ""}</small>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function LiveEvidence({ a }) {
  const sources = asArr(a.research);
  if (!sources.length) return null;
  return (
    <Section kicker="Live source ledger" title={`${sources.length} external sources reviewed`}
      desc="Independent material gathered for this investigation. Each entry links to the original source."
      badge={`${sources.length} SOURCES`} id="live-evidence">
      <div className="hbmc-card">
        {sources.slice(0, 24).map((r, i) => (
          <a className="srcRow" href={r.url} target="_blank" rel="noreferrer" key={i}>
            <i>{String(i + 1).padStart(2, "0")}</i>
            <span><b>{r.title}</b><small>{[r.label, r.domain, r.published, r.sourceStatus].filter(Boolean).join(" · ")}</small></span>
            <em>OPEN <ExternalLink size={11} style={{ display: "inline" }} /></em>
          </a>
        ))}
      </div>
    </Section>
  );
}

function MemoDeck({ a }) {
  const tabs = ["brief", "diagnosis", "options", "decision", "execution"];
  const memo = a.memo || {};
  const [active, setActive] = useState("brief");
  const evidence = asArr(a.evidence);
  const [sel, setSel] = useState(null);
  const sec = memo[active] || {};
  return (
    <Section kicker="Document-derived decision memo" title={memo.brief?.title || "Executive memorandum"}
      desc="Generated after server-side extraction and evidence reconciliation." id="memo">
      <div className="memoDeck">
        <div className="memoTabs">
          {tabs.map((t) => <button key={t} className={active === t ? "on" : ""} onClick={() => setActive(t)} data-testid={`memo-tab-${t}`}>{t}</button>)}
        </div>
        <div className="memoBody">
          <div className="kicker"><span>{sec.kicker}</span><span>{active === "decision" ? "RECOMMENDATION" : "HBM INFERENCE"}</span></div>
          <h3>{sec.title}</h3>
          <p>{sec.body}</p>
          {asArr(sec.cites).length > 0 && (
            <div className="cites">{asArr(sec.cites).map((id) => <button key={id} onClick={() => setSel(id)}>[{id}]</button>)}</div>
          )}
        </div>
      </div>
      {sel && evidence.find((e) => e.id === sel) && (
        <div className="hbmc-card" style={{ marginTop: 12 }}>
          {(() => { const e = evidence.find((x) => x.id === sel); return (
            <><div className="lbl"><span>{e.tag} · CITATION [{e.id}]</span><b onClick={() => setSel(null)} style={{ cursor: "pointer" }}>CLOSE ×</b></div>
              <h3 className="sub">{e.title}</h3><p style={{ fontSize: 14 }}>“{e.quote}”</p>
              <small className="dim">{e.source} · {e.loc} · extraction {e.quality}%</small></>
          ); })()}
        </div>
      )}
      <div className="hbmc-grid g2" style={{ marginTop: 14 }}>
        {evidence.map((e) => (
          <div className="hbmc-card" key={e.id} onClick={() => setSel(e.id)} style={{ cursor: "pointer" }}>
            <div className="lbl"><span>{e.tag}</span><b>[{e.id}]</b></div>
            <h3 className="sub" style={{ fontSize: 15 }}>{e.title}</h3>
            <p style={{ fontSize: 13 }}>{e.quote}</p>
            <small className="dim">{e.source} · {e.loc}</small>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function ConsultReport({ a }) {
  if (!a) return null;
  return (
    <div data-testid="consult-report">
      <MetricsStrip metrics={a.metrics} />
      <ConfidenceHeader c={a.confidence} />
      <DecisionDashboard a={a} />
      <DecisionIntelligence a={a} />
      <PeerBenchmark pb={a.peerBenchmark} />
      <AdvancedCharts a={a} />
      <CrunchingLab a={a} />
      <ResearchConsole a={a} />
      <MethodologyLab a={a} />
      <ResearchAudit a={a} />
      <EvidenceLedger a={a} />
      <LiveEvidence a={a} />
      <MemoDeck a={a} />
    </div>
  );
}
